<script>
    import {createEventDispatcher, tick} from "svelte";

    export let default_locations = [];
    export let all_locations = [];
    export let location = null;
    export let placeholder = '';
    export let required = false;

    const dispatch = createEventDispatcher();

    let open = false;
    let query = "";
    let inputEl = null;
    let rootEl = null;

    let activeIndex = -1;

    $: normalizedQuery = query.trim().toLowerCase();

    $: items = (normalizedQuery.length > 0 ? all_locations : default_locations)
        .filter((l) => l && l.name && l.name.toLowerCase().includes(normalizedQuery))
        .reduce((acc, cur) => {
            if (!acc.some((x) => x.id === cur.id)) acc.push(cur);
            return acc;
        }, []);

    // όταν δεν υπάρχουν matches αλλά υπάρχει query, δείχνουμε ένα "free text" option
    $: hasFreeTextOption = query.trim().length > 0 && items.length === 0;

    $: if (open) {
        // αν δεν έχει items αλλά έχει free-text option, βάζουμε activeIndex=0
        activeIndex = (items.length > 0 || hasFreeTextOption) ? 0 : -1;
    } else {
        activeIndex = -1;
    }

    function openDropdown(focus = true) {
        open = true;
        if (focus) tick().then(() => inputEl && inputEl.focus());
    }

    // ✅ δεν καθαρίζει πάντα το query
    function closeDropdown({clearQuery = false} = {}) {
        open = false;
        if (clearQuery) query = "";
    }

    function select(item) {
        location = item;
        dispatch("change", item);
        closeDropdown({clearQuery: true});
    }

    function selectFreeText() {
        const name = query.trim();
        if (!name) return;

        const item = {id: null, name};
        location = item;
        dispatch("change", item);
        closeDropdown({clearQuery: true});
    }

    function clearSelection() {
        location = null;
        dispatch("change", null);
        openDropdown(true);
    }

    function onDocumentPointerDown(e) {
        if (!open) return;
        if (rootEl && !rootEl.contains(e.target)) closeDropdown({clearQuery: false}); // ✅ κρατάει το query
    }

    function onLabelClick() {
        query = ""; // show defaults
        openDropdown(true);
    }

    function onInput(e) {
        query = e.target.value;
        openDropdown(false);
    }

    function moveActive(delta) {
        if (!open) openDropdown(false);

        // total options = items.length + (freeText?1:0)
        const total = items.length + (hasFreeTextOption ? 1 : 0);
        if (!total) return;

        activeIndex = Math.max(0, Math.min(total - 1, activeIndex + delta));

        const el = document.getElementById(`lc-opt-${activeIndex}`);
        el && el.scrollIntoView({block: "nearest"});
    }

    function onKeyDown(e) {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            e.preventDefault();
            openDropdown(true);
            return;
        }

        if (!open) return;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                moveActive(1);
                break;
            case "ArrowUp":
                e.preventDefault();
                moveActive(-1);
                break;
            case "Enter":
                e.preventDefault();
                if (hasFreeTextOption) {
                    // αν δεν υπάρχουν matches, το μοναδικό option είναι free text (index 0)
                    selectFreeText();
                } else if (activeIndex >= 0 && items[activeIndex]) {
                    select(items[activeIndex]);
                }
                break;
            case "Escape":
                closeDropdown({clearQuery: false}); // ✅ κρατάει το query
                break;
            case "Tab":
                closeDropdown({clearQuery: false}); // ✅ κρατάει το query
                break;
        }
    }
</script>

<svelte:document on:pointerdown={onDocumentPointerDown}/>

<div class="position-relative" bind:this={rootEl} on:keydown={onKeyDown}>
    {#if location && !open}
        <button type="button" class="form-control d-flex justify-content-between" on:click={onLabelClick}>
            <span class="text-truncate">{location.name}</span>
            <span class="opacity-75">▾</span>
        </button>
    {:else}
        <div class="input-group">
            <input
                    class="form-control"
                    bind:this={inputEl}
                    {placeholder}
                    value={query}
                    on:input={onInput}
                    on:focus={() => openDropdown(false)}
                    autocomplete="off"
                    {required}
            />
            {#if location}
                <button class="btn btn-outline-secondary" type="button" on:click={clearSelection}>✕</button>
            {/if}
        </div>

        {#if open}
            <div class="dropdown-menu bg-white show w-100 mt-1" style="max-height:240px;overflow:auto;">

                {#if hasFreeTextOption}
                    <button
                            id="lc-opt-0"
                            type="button"
                            class={"dropdown-item " + (activeIndex === 0 ? "active" : "")}
                            on:mousemove={() => (activeIndex = 0)}
                            on:click={selectFreeText}
                    >
                        Χρήση: <strong>{query}</strong>
                    </button>
                    <div class="dropdown-item text-muted small">
                        Δεν βρέθηκαν αποτελέσματα στα διαθέσιμα locations
                    </div>
                {:else}
                    {#each items as item, i (item.id)}
                        <button
                                id={`lc-opt-${i}`}
                                type="button"
                                class={"dropdown-item " + (i === activeIndex ? "active" : "")}
                                on:mousemove={() => (activeIndex = i)}
                                on:click={() => select(item)}
                        >
                            {item.name}
                        </button>
                    {/each}

                    {#if items.length === 0}
                        <div class="dropdown-item text-muted">Δεν βρέθηκαν αποτελέσματα</div>
                    {/if}
                {/if}
            </div>
        {/if}
    {/if}
</div>
